/**
 * Tooth wireframe traced from the photo.
 * The server locates each upper crown, segments it from the photo's pixels,
 * and returns the mask outlines. This file crops the smile, sends it, and
 * draws those outlines. Nothing here invents or idealizes a tooth shape.
 */

import api from "../../api";
import { detectFaceLandmarks } from "./faceDetection";
import { toPixelCoords } from "./landmarkProcessing";
import { extractSmileRegion } from "./smileRegionExtraction";
import { expandPolygon } from "./lipCropExtractor";
import type { Point2D } from "./types";

export interface TracedTooth {
  /** Outline in the analyzed crop's pixel space */
  contour: Point2D[];
  label?: string;
}

export interface TraceWireframeResult {
  dataUrl: string;
  toothCount: number;
  width: number;
  height: number;
  teeth: TracedTooth[];
  /** Model that located the teeth; outlines always come from the photo's pixels */
  detector: string;
  /** Natural size of the photo that was traced */
  imageWidth: number;
  imageHeight: number;
  /** One closed outline per tooth, left → right, in the traced photo's own pixels */
  sourceContours: Point2D[][];
}

/** Crop width sent for detection. Teeth fill the frame at this size. */
const DETECT_CROP_WIDTH = 1024;
const MAX_DETECT_UPSCALE = 4;
/** Below this many pixels across the mouth opening, enamel edges are too soft to trace. */
const MIN_MOUTH_WIDTH = 90;

interface DetectionCrop {
  blob: Blob;
  /** Crop rectangle in the source photo's pixels */
  box: { x: number; y: number; width: number; height: number };
  imageWidth: number;
  imageHeight: number;
  /** Inner-lip opening normalized to the crop, or null when no face was found */
  mouthPolygon: Point2D[] | null;
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error("Failed to load image for tooth tracing"));
    img.src = src;
  });
}

function canvasToBlob(canvas: HTMLCanvasElement): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error("Could not encode the smile crop"))),
      "image/jpeg",
      0.95
    );
  });
}

/**
 * Tight crop around the lip opening, upscaled so the teeth fill the frame.
 * The vision model sees the enamel edges instead of a whole face.
 */
async function buildDetectionCrop(img: HTMLImageElement): Promise<DetectionCrop> {
  const width = img.naturalWidth || img.width;
  const height = img.naturalHeight || img.height;

  const landmarks = await detectFaceLandmarks(img).catch(() => null);
  const region = landmarks
    ? extractSmileRegion(toPixelCoords(landmarks, width, height), width, height)
    : null;

  let box = { x: 0, y: 0, width, height };
  let mouth: Point2D[] | null = null;

  if (region && region.innerPolygon.length >= 3) {
    const inner = region.innerPolygon;
    const xs = inner.map((p) => p.x);
    const ys = inner.map((p) => p.y);
    const minX = Math.min(...xs);
    const maxX = Math.max(...xs);
    const minY = Math.min(...ys);
    const maxY = Math.max(...ys);
    const mw = maxX - minX;
    const mh = Math.max(1, maxY - minY);
    if (mw < MIN_MOUTH_WIDTH) {
      throw new Error(
        `The smile is only ${Math.round(mw)} px wide in this photo. Use a higher-resolution photo (at least ${MIN_MOUTH_WIDTH} px across the mouth) for an accurate trace.`
      );
    }
    const padX = mw * 0.1;
    const padY = Math.max(mh * 0.45, mw * 0.06);
    const x0 = Math.max(0, Math.floor(minX - padX));
    const y0 = Math.max(0, Math.floor(minY - padY));
    const x1 = Math.min(width, Math.ceil(maxX + padX));
    const y1 = Math.min(height, Math.ceil(maxY + padY));
    box = { x: x0, y: y0, width: x1 - x0, height: y1 - y0 };
    mouth = expandPolygon(inner, 1.06).map((p) => ({
      x: (p.x - box.x) / box.width,
      y: (p.y - box.y) / box.height,
    }));
  }

  const scale = Math.min(
    MAX_DETECT_UPSCALE,
    Math.max(1, DETECT_CROP_WIDTH / box.width)
  );
  const canvas = document.createElement("canvas");
  canvas.width = Math.round(box.width * scale);
  canvas.height = Math.round(box.height * scale);
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Could not prepare the smile crop");
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = "high";
  ctx.drawImage(
    img,
    box.x,
    box.y,
    box.width,
    box.height,
    0,
    0,
    canvas.width,
    canvas.height
  );

  return {
    blob: await canvasToBlob(canvas),
    box,
    imageWidth: width,
    imageHeight: height,
    mouthPolygon: mouth,
  };
}

/** Closed curve through the midpoints, so dense mask outlines draw without corners. */
function strokeSmoothClosed(
  ctx: CanvasRenderingContext2D,
  points: Point2D[]
): void {
  const n = points.length;
  if (n < 3) return;
  const mid = (a: Point2D, b: Point2D) => ({ x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 });
  const start = mid(points[n - 1], points[0]);
  ctx.beginPath();
  ctx.moveTo(start.x, start.y);
  for (let i = 0; i < n; i++) {
    const p = points[i];
    const m = mid(p, points[(i + 1) % n]);
    ctx.quadraticCurveTo(p.x, p.y, m.x, m.y);
  }
  ctx.closePath();
  ctx.stroke();
}

interface RenderedWireframe {
  dataUrl: string;
  width: number;
  height: number;
}

/**
 * Draw closed outlines (any pixel space) as the white-on-black wireframe
 * filter used by the rest of the pipeline.
 */
export function renderWireframeFromContours(contours: Point2D[][]): RenderedWireframe {
  const { dataUrl, width, height } = renderWireframe(
    contours.map((contour) => ({ contour })),
    "edited"
  );
  return { dataUrl, width, height };
}

function renderWireframe(
  teeth: TracedTooth[],
  detector: string
): Omit<TraceWireframeResult, "imageWidth" | "imageHeight" | "sourceContours"> {
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const tooth of teeth) {
    for (const p of tooth.contour) {
      minX = Math.min(minX, p.x);
      minY = Math.min(minY, p.y);
      maxX = Math.max(maxX, p.x);
      maxY = Math.max(maxY, p.y);
    }
  }

  const contentW = Math.max(1, maxX - minX);
  const contentH = Math.max(1, maxY - minY);
  const outW = 1024;
  const pad = Math.round(outW * 0.035);
  const scale = (outW - pad * 2) / contentW;
  const outH = Math.round(contentH * scale) + pad * 2;

  const canvas = document.createElement("canvas");
  canvas.width = outW;
  canvas.height = outH;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Could not draw the traced wireframe");
  ctx.fillStyle = "#000";
  ctx.fillRect(0, 0, outW, outH);
  ctx.strokeStyle = "#fff";
  ctx.lineWidth = 2.5;
  ctx.lineJoin = "round";
  ctx.lineCap = "round";

  for (const tooth of teeth) {
    strokeSmoothClosed(
      ctx,
      tooth.contour.map((p) => ({
        x: (p.x - minX) * scale + pad,
        y: (p.y - minY) * scale + pad,
      }))
    );
  }

  return {
    dataUrl: canvas.toDataURL("image/png"),
    toothCount: teeth.length,
    width: outW,
    height: outH,
    teeth,
    detector,
  };
}

/**
 * Segment every visible upper crown in the photo and draw its outline
 * as a white-on-black wireframe filter.
 */
export async function traceSmileWireframe(
  imageSrc: string
): Promise<TraceWireframeResult> {
  const img = await loadImage(imageSrc);
  const crop = await buildDetectionCrop(img);

  const formData = new FormData();
  formData.append("image", crop.blob, "smile-for-tooth-detection.jpg");
  if (crop.mouthPolygon) {
    formData.append(
      "mouthPolygon",
      JSON.stringify(crop.mouthPolygon.map((p) => [p.x, p.y]))
    );
  }

  const response = await api.post("/smile-design/detect-tooth-outlines", formData, {
    timeout: 180000,
  });
  const data = response.data?.data as
    | {
        teeth?: { points?: Point2D[]; label?: string }[];
        width?: number;
        height?: number;
        detector?: string;
      }
    | undefined;

  const width = Number(data?.width) || 1;
  const height = Number(data?.height) || 1;
  const teeth: TracedTooth[] = (data?.teeth || [])
    .filter((tooth) => (tooth.points?.length ?? 0) >= 6)
    .map((tooth) => ({
      label: tooth.label,
      contour: (tooth.points as Point2D[]).map((p) => ({
        x: p.x * width,
        y: p.y * height,
      })),
    }));

  if (teeth.length < 4) {
    throw new Error("Could not separate enough upper teeth in this photo");
  }
  const rendered = renderWireframe(teeth, data?.detector ?? "unknown");
  const { box } = crop;
  return {
    ...rendered,
    imageWidth: crop.imageWidth,
    imageHeight: crop.imageHeight,
    sourceContours: teeth.map((tooth) =>
      tooth.contour.map((p) => ({
        x: box.x + (p.x / width) * box.width,
        y: box.y + (p.y / height) * box.height,
      }))
    ),
  };
}
